import { CommonModule } from "@angular/common";
import { Component, inject, input, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import {
  Coupon,
  CouponListFilters,
  CouponSortField,
  CouponStatus,
} from "@models/coupon";
import { CouponsService } from "@services/coupons.service";
import { SnackBarService } from "@services/snack-bar.service";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { PaginatorState } from "primeng/paginator";
import { SkeletonModule } from "primeng/skeleton";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ConfirmActionModalComponent } from "../../shared/modals/confirm-action-modal/confirm-action-modal.component";
import { StatusSummaryCardComponent } from "../../shared/components/cards/status-summary-card/status-summary-card.component";
import { PaginatorReportComponent } from "../../shared/components/paginator-report/paginator-report.component";
import { CouponFormModalComponent } from "./coupon-form-modal/coupon-form-modal.component";
import { CouponTableComponent } from "./coupon-table/coupon-table.component";

@Component({
  selector: "app-producer-coupons-page",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    PaginatorReportComponent,
    StatusSummaryCardComponent,
    CouponTableComponent,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SkeletonModule,
  ],
  templateUrl: "./producer-coupons-page.component.html",
  styleUrl: "./producer-coupons-page.component.scss",
})
export class ProducerCouponsPageComponent {
  private readonly _couponsService = inject(CouponsService);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);
  private readonly _snackbar = inject(SnackBarService);

  readonly eventId = input.required<number>();
  readonly readOnly = input(false);

  tableFilter = new FormControl("", { nonNullable: true });

  activeStatusFilter: CouponStatus | null = null;
  sort: CouponSortField | null = null;
  direction: "asc" | "desc" = "desc";

  readonly isLoading = signal(false);

  private readonly _page$ = new BehaviorSubject<PaginatorState | null>(null);
  private readonly _statusFilter$ = new BehaviorSubject<CouponStatus | null>(
    null,
  );
  private readonly _sort$ = new BehaviorSubject<{
    sort: CouponSortField | null;
    direction: "asc" | "desc";
  }>({ sort: null, direction: "desc" });
  private readonly _refresh$ = new BehaviorSubject<void>(undefined);

  private readonly _minSearchLength = 3;

  private readonly _search$ = this.tableFilter.valueChanges.pipe(
    startWith(""),
    debounceTime(500),
    map((value) => value.trim()),
    filter(
      (value) => value.length === 0 || value.length >= this._minSearchLength,
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  coupons$ = combineLatest([
    this._page$,
    this._search$,
    this._statusFilter$,
    this._sort$,
    this._refresh$,
  ]).pipe(
    switchMap(([page, search, status, sort]) => {
      this.isLoading.set(true);
      const filters: CouponListFilters = {
        page: (page?.page ?? 0) + 1,
        per_page: page?.rows ?? 10,
      };
      if (search) filters.query = search;
      if (status) filters.status = status;
      if (sort.sort) {
        filters.sort = sort.sort;
        filters.direction = sort.direction;
      }
      return this._couponsService
        .list(this.eventId(), filters)
        .pipe(finalize(() => this.isLoading.set(false)));
    }),
  );

  get hasActiveFilters(): boolean {
    return (
      this.activeStatusFilter !== null ||
      this.tableFilter.value.trim().length >= this._minSearchLength
    );
  }

  get showSearchHint(): boolean {
    const length = this.tableFilter.value.trim().length;
    return length > 0 && length < this._minSearchLength;
  }

  handlePageChange(page: PaginatorState) {
    this._page$.next(page);
  }

  // Cartões: "Criados" = sem filtro de status; "Ativos" = active; "Encerrados"
  // = closed. Clicar de novo no card ativo limpa o filtro.
  handleStatusFilter(status: CouponStatus | null) {
    this.activeStatusFilter =
      this.activeStatusFilter === status ? null : status;
    this._statusFilter$.next(this.activeStatusFilter);
  }

  handleSort(field: CouponSortField) {
    if (this.sort === field) {
      this.direction = this.direction === "asc" ? "desc" : "asc";
    } else {
      this.sort = field;
      this.direction = "desc";
    }
    this._sort$.next({ sort: this.sort, direction: this.direction });
  }

  clearFilters() {
    this.tableFilter.setValue("");
    this.activeStatusFilter = null;
    this._statusFilter$.next(null);
  }

  handleViewDetails(coupon: Coupon) {
    this._router.navigate(["/coupons", coupon.id], {
      queryParams: { event_id: coupon.event_id },
    });
  }

  openCreateModal() {
    this._openForm({ eventId: this.eventId() });
  }

  openEditModal(coupon: Coupon) {
    this._openForm({ eventId: this.eventId(), coupon });
  }

  handleDeactivate(coupon: Coupon) {
    const dialogRef = this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Desativar cupom?",
        modalContent: `Tem certeza que deseja desativar o cupom ${coupon.code}? Esta ação não pode ser desfeita.`,
        confirmMessage: "Desativar",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() =>
          this._couponsService.deactivate(coupon.event_id, coupon.id).pipe(
            tap(() => {
              this._snackbar.openSnackBar(
                "Cupom desativado com sucesso.",
                "Fechar",
                "success",
              );
              this._refresh$.next();
            }),
            catchError((err) => {
              // 422: cupom com utilização não pode ser desativado, só encerrado.
              if (err?.status === 422) {
                this._offerClose(coupon, err?.error?.error);
              } else {
                this._snackbar.openSnackBar(
                  err?.error?.error ?? "Não foi possível desativar o cupom.",
                  "Fechar",
                  "error",
                );
              }
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  private _offerClose(coupon: Coupon, reason?: string) {
    const dialogRef = this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Encerrar cupom?",
        modalContent:
          (reason ??
            "Este cupom já possui utilizações e não pode ser desativado.") +
          " Deseja encerrá-lo? O histórico de quem já usou será mantido.",
        confirmMessage: "Encerrar",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() =>
          this._couponsService.close(coupon.event_id, coupon.id).pipe(
            tap(() => {
              this._snackbar.openSnackBar(
                "Cupom encerrado com sucesso.",
                "Fechar",
                "success",
              );
              this._refresh$.next();
            }),
            catchError((err) => {
              this._snackbar.openSnackBar(
                err?.error?.error ?? "Não foi possível encerrar o cupom.",
                "Fechar",
                "error",
              );
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  private _openForm(data: { eventId: number; coupon?: Coupon }) {
    this._dialog
      .open(CouponFormModalComponent, {
        width: "600px",
        autoFocus: false,
        data,
      })
      .afterClosed()
      .pipe(
        filter((saved): saved is Coupon => !!saved),
        tap(() => this._refresh$.next()),
      )
      .subscribe();
  }
}
